import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  VisibilityOutlined as ViewIcon,
} from '@mui/icons-material';
import { CollectionSiteSchema, CollectionSiteType } from '../../types/collection-site.type';
import { formatSiteLocation, formatSiteDateTime } from '../../helpers/collection-site.helpers';
import { SetupCoachmark } from '../selfSetup/SetupCoachmark';

export interface CollectionSiteCardProps {
  site: CollectionSiteSchema;
  onView: (site: CollectionSiteSchema) => void;
  onEdit: (site: CollectionSiteSchema) => void;
  onDelete: (id: string) => void;
  /** Knowledge Base "Go to Edit or delete" tips on this card. */
  helpRowActionsPhase?: 'edit' | 'delete' | null;
  onHelpRowActionsNext?: () => void;
  onHelpRowActionsDismiss?: () => void;
}

function DetailLine({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(88px, 38%) 1fr',
        gap: 1,
        alignItems: 'baseline',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'text.secondary',
        }}
      >
        {label}
      </Typography>
      <Box sx={{ minWidth: 0, textAlign: 'end' }}>{children}</Box>
    </Box>
  );
}

const CollectionSiteCard: React.FC<CollectionSiteCardProps> = ({
  site,
  onView,
  onEdit,
  onDelete,
  helpRowActionsPhase = null,
  onHelpRowActionsNext,
  onHelpRowActionsDismiss,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const displayName = site.data?.name || t('common.siteFallback', { id: site.id.slice(0, 8) });
  const siteType = site.data?.type as CollectionSiteType | undefined;
  const locationLabel = formatSiteLocation(site);
  const typeLabel = siteType
    ? t(`collectionSites.types.${siteType}`, { defaultValue: siteType })
    : null;

  const actionButtonSx = {
    width: 32,
    height: 32,
    color: theme.palette.text.secondary,
  };

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        transition: 'border-color 0.2s ease',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.4),
        },
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.primary.main, 0.06),
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, lineHeight: 1.3, m: 0, minWidth: 0, color: 'text.primary' }}
          noWrap
          title={displayName}
        >
          {displayName}
        </Typography>

        <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
          <Tooltip title={t('collectionSites.view')}>
            <IconButton
              size="small"
              onClick={() => onView(site)}
              sx={{
                ...actionButtonSx,
                color: theme.palette.primary.main,
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
              }}
            >
              <ViewIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          {helpRowActionsPhase === 'edit' ? (
            <SetupCoachmark
              open
              title={t('collectionSites.editSiteHintTitle')}
              subtitle={t('collectionSites.editSiteHint')}
              nextLabel={t('selfSetup.tourNext')}
              onNext={() => onHelpRowActionsNext?.()}
              onDismiss={() => onHelpRowActionsDismiss?.()}
            >
              <Tooltip title={t('common.edit')}>
                <IconButton
                  size="small"
                  onClick={() => onEdit(site)}
                  sx={{
                    ...actionButtonSx,
                    '&:hover': {
                      color: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  <EditIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </SetupCoachmark>
          ) : (
            <Tooltip title={t('common.edit')}>
              <IconButton
                size="small"
                onClick={() => onEdit(site)}
                sx={{
                  ...actionButtonSx,
                  '&:hover': {
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                <EditIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          {helpRowActionsPhase === 'delete' ? (
            <SetupCoachmark
              open
              title={t('collectionSites.deleteSiteHintTitle')}
              subtitle={t('collectionSites.deleteSiteHint')}
              nextLabel={t('selfSetup.tourDone')}
              onNext={() => onHelpRowActionsDismiss?.()}
              onDismiss={() => onHelpRowActionsDismiss?.()}
            >
              <Tooltip title={t('common.delete')}>
                <IconButton
                  size="small"
                  onClick={() => onDelete(site.id)}
                  sx={{
                    ...actionButtonSx,
                    '&:hover': {
                      color: theme.palette.error.main,
                      bgcolor: alpha(theme.palette.error.main, 0.08),
                    },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </SetupCoachmark>
          ) : (
            <Tooltip title={t('common.delete')}>
              <IconButton
                size="small"
                onClick={() => onDelete(site.id)}
                sx={{
                  ...actionButtonSx,
                  '&:hover': {
                    color: theme.palette.error.main,
                    bgcolor: alpha(theme.palette.error.main, 0.08),
                  },
                }}
              >
                <DeleteIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>

      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack spacing={1.25}>
          <DetailLine label={t('collectionSites.card.type')}>
            {typeLabel ? (
              <Chip
                size="small"
                label={typeLabel}
                sx={{
                  height: 24,
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                —
              </Typography>
            )}
          </DetailLine>

          <DetailLine label={t('collectionSites.card.location')}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: locationLabel ? 'text.primary' : 'text.secondary',
              }}
              noWrap
              title={locationLabel ?? t('collectionSites.card.locationNotSet')}
            >
              {locationLabel ?? t('collectionSites.card.locationNotSet')}
            </Typography>
          </DetailLine>

          <DetailLine label={t('collectionSites.card.updated')}>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ fontWeight: 500 }}
              noWrap
              title={formatSiteDateTime(site.updated_at)}
            >
              {formatSiteDateTime(site.updated_at)}
            </Typography>
          </DetailLine>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CollectionSiteCard;
